import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Leads API
export const createLead = async (leadData) => {
  const response = await axios.post(`${API_URL}/leads`, leadData);
  return response.data;
};

export const getLeads = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/leads`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const updateLead = async (leadId, updateData) => {
  const response = await axios.put(`${API_URL}/leads/${leadId}`, updateData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminCreateLead = async (leadData) => {
  const response = await axios.post(`${API_URL}/leads/admin-create`, leadData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const bulkUploadLeads = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/leads/bulk-upload`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const addLeadNote = async (leadId, text) => {
  const response = await axios.post(`${API_URL}/leads/${leadId}/add-note`, { text }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getLeadNotes = async (leadId) => {
  const response = await axios.get(`${API_URL}/leads/${leadId}/notes`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const convertLeadToApplication = async (leadId) => {
  const response = await axios.post(`${API_URL}/leads/${leadId}/convert`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteLead = async (leadId) => {
  const response = await axios.delete(`${API_URL}/leads/${leadId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Dealer Applications API
export const submitDealerApplication = async (applicationData) => {
  const response = await axios.post(`${API_URL}/dealer-applications`, applicationData);
  return response.data;
};

export const getDealerApplications = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/dealer-applications`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const getApplicationAttributionSummary = async (limit = 10) => {
  const response = await axios.get(`${API_URL}/dealer-applications/attribution-summary`, {
    headers: getAuthHeader(),
    params: { limit }
  });
  return response.data;
};

export const updateApplicationStatus = async (appId, status) => {
  const response = await axios.put(
    `${API_URL}/dealer-applications/${appId}/status?status=${status}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const updateApplicationSubAgentId = async (appId, subAgentId) => {
  const response = await axios.put(
    `${API_URL}/dealer-applications/${appId}/sub-agent-id`,
    { sub_agent_id: subAgentId },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const convertApplicationToDealer = async (appId) => {
  const response = await axios.post(
    `${API_URL}/dealer-applications/${appId}/convert-to-dealer`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const adminCreateApplication = async (appData) => {
  const response = await axios.post(`${API_URL}/dealer-applications/admin-create`, appData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const bulkUploadApplications = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealer-applications/bulk-upload`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

// Dealers API
export const getDealers = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/dealers`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const updateDealerStatus = async (dealerId, status, reassignments = null) => {
  const body = { status };
  if (Array.isArray(reassignments)) body.reassignments = reassignments;
  const response = await axios.put(
    `${API_URL}/dealers/${dealerId}/status`,
    body,
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getDealerDetail = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDealerProfile = async (dealerId, profileData) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}`, profileData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const resetDealerPassword = async (dealerId, newPassword) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/reset-password`, { new_password: newPassword }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDealerLocations = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/locations`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const assignDealerLocations = async (dealerId, locationIds) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/locations`, { location_ids: locationIds }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDealerNotes = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/notes`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const addDealerNote = async (dealerId, text, type = "note") => {
  const response = await axios.post(`${API_URL}/dealers/${dealerId}/notes`, { text, type }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDealerBilling = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/billing`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDealerBilling = async (dealerId, billingData) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/billing`, billingData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDealerDocuments = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/documents`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const uploadDealerDocument = async (dealerId, file, docType = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealers/${dealerId}/documents?doc_type=${docType}`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const deleteDealerDocument = async (dealerId, docId) => {
  const response = await axios.delete(`${API_URL}/dealers/${dealerId}/documents/${docId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// VidaPay
export const getDealerVidapay = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/vidapay`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDealerVidapay = async (dealerId, data) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/vidapay`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Application Management
export const updateApplication = async (appId, data) => {
  const response = await axios.put(`${API_URL}/dealer-applications/${appId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getApplicationDocuments = async (appId) => {
  const response = await axios.get(`${API_URL}/dealer-applications/${appId}/documents`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const uploadApplicationDocument = async (appId, file, docType = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealer-applications/${appId}/documents?doc_type=${docType}`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const deleteApplicationDocument = async (appId, docId) => {
  const response = await axios.delete(`${API_URL}/dealer-applications/${appId}/documents/${docId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// User Management
export const getAdminUsers = async () => {
  const response = await axios.get(`${API_URL}/admin/users`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createAdminUser = async (userData) => {
  const response = await axios.post(`${API_URL}/admin/users`, userData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateAdminUser = async (userId, userData) => {
  const response = await axios.put(`${API_URL}/admin/users/${userId}`, userData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteAdminUser = async (userId) => {
  const response = await axios.delete(`${API_URL}/admin/users/${userId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMyPermissions = async () => {
  const response = await axios.get(`${API_URL}/admin/permissions`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Dealer Portal Documents
export const getMyDealerDocuments = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-documents`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const uploadMyDealerDocument = async (file, docType = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealer/my-documents?doc_type=${docType}`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const getRequiredDocuments = async () => {
  const response = await axios.get(`${API_URL}/dealer/required-documents`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteMyDealerDocument = async (docId) => {
  const response = await axios.delete(`${API_URL}/dealer/my-documents/${docId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pre-Approved Documents
export const getPreApprovedData = async () => {
  const response = await axios.get(`${API_URL}/dealer/pre-approved-data`, { headers: getAuthHeader() });
  return response.data;
};

export const updatePreApprovedData = async (data) => {
  const response = await axios.put(`${API_URL}/dealer/pre-approved-data`, data, { headers: getAuthHeader() });
  return response.data;
};

export const getPreApprovedDocuments = async () => {
  const response = await axios.get(`${API_URL}/dealer/pre-approved-documents`, { headers: getAuthHeader() });
  return response.data;
};

export const uploadPreApprovedDocument = async (file, docType) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealer/pre-approved-documents?doc_type=${docType}`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const deletePreApprovedDocument = async (docId) => {
  const response = await axios.delete(`${API_URL}/dealer/pre-approved-documents/${docId}`, { headers: getAuthHeader() });
  return response.data;
};

// Orders API
export const createOrder = async (orderData) => {
  const response = await axios.post(`${API_URL}/orders`, orderData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getOrders = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/orders`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await axios.put(
    `${API_URL}/orders/${orderId}/status?status=${status}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Commissions API
export const getCommissions = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/commissions`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

// Training API
export const getTrainingMaterials = async (category = null) => {
  const params = category ? { category } : {};
  const response = await axios.get(`${API_URL}/training`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

// Promotions API
export const getPromotions = async (activeOnly = false) => {
  const params = activeOnly ? { active_only: true } : {};
  const response = await axios.get(`${API_URL}/promotions`, { params });
  return response.data;
};

export const createPromotion = async (promotionData) => {
  const response = await axios.post(`${API_URL}/promotions`, promotionData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updatePromotion = async (promoId, promotionData) => {
  const response = await axios.put(`${API_URL}/promotions/${promoId}`, promotionData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deletePromotion = async (promoId) => {
  const response = await axios.delete(`${API_URL}/promotions/${promoId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Analytics API
export const getAdminDashboard = async () => {
  const response = await axios.get(`${API_URL}/analytics/admin-dashboard`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDealerDashboard = async () => {
  const response = await axios.get(`${API_URL}/analytics/dealer-dashboard`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getCommissionHistory = async () => {
  const response = await axios.get(`${API_URL}/analytics/commission-history`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Locations API
export const getLocations = async (activeOnly = true) => {
  const params = { active_only: activeOnly };
  const response = await axios.get(`${API_URL}/locations`, { params });
  return response.data;
};

export const getComingSoonLocations = async (skipGeocode = false) => {
  const response = await axios.get(`${API_URL}/locations/coming-soon`, skipGeocode ? { params: { skip_geocode: true } } : undefined);
  return response.data;
};

export const getLocation = async (locationId) => {
  const isSlug = locationId.includes('/');
  const url = isSlug ? `${API_URL}/locations/by-slug/${locationId}` : `${API_URL}/locations/${locationId}`;
  const response = await axios.get(url);
  return response.data;
};

export const getLocationOwner = async (locationId) => {
  const response = await axios.get(`${API_URL}/locations/${locationId}/owner`, { headers: getAuthHeader() });
  return response.data;
};

export const updateLocationContacts = async (locationId, data) => {
  const response = await axios.put(`${API_URL}/dealer/locations/${locationId}/contacts`, data, { headers: getAuthHeader() });
  return response.data;
};

export const updateLocationVidapay = async (locationId, data) => {
  const response = await axios.put(`${API_URL}/dealer/locations/${locationId}/vidapay`, data, { headers: getAuthHeader() });
  return response.data;
};

export const createLocation = async (locationData) => {
  const response = await axios.post(`${API_URL}/locations`, locationData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateLocation = async (locationId, locationData) => {
  const response = await axios.put(`${API_URL}/locations/${locationId}`, locationData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteLocation = async (locationId) => {
  const response = await axios.delete(`${API_URL}/locations/${locationId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Announcements API
export const getAnnouncements = async (activeOnly = true, target = null) => {
  const params = { active_only: activeOnly };
  if (target) params.target = target;
  const response = await axios.get(`${API_URL}/announcements`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const createAnnouncement = async (announcementData) => {
  const response = await axios.post(`${API_URL}/announcements`, announcementData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateAnnouncement = async (announcementId, announcementData) => {
  const response = await axios.put(`${API_URL}/announcements/${announcementId}`, announcementData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteAnnouncement = async (announcementId) => {
  const response = await axios.delete(`${API_URL}/announcements/${announcementId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pages API
export const getAllPages = async () => {
  const response = await axios.get(`${API_URL}/pages`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPageContent = async (pageSlug) => {
  const response = await axios.get(`${API_URL}/pages/${pageSlug}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createPageContent = async (contentData) => {
  const response = await axios.post(`${API_URL}/pages`, contentData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updatePageContent = async (contentId, contentData) => {
  const response = await axios.put(`${API_URL}/pages/${contentId}`, contentData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deletePageContent = async (contentId) => {
  const response = await axios.delete(`${API_URL}/pages/${contentId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// SEO API
export const getAllSEOSettings = async () => {
  const response = await axios.get(`${API_URL}/seo`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getSEOSettings = async (pageSlug) => {
  const response = await axios.get(`${API_URL}/seo/${pageSlug}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateSEOSettings = async (pageSlug, seoData) => {
  const response = await axios.put(`${API_URL}/seo/${pageSlug}`, seoData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPublicSEO = async (pageSlug) => {
  const response = await axios.get(`${API_URL}/seo/public/${pageSlug}`);
  return response.data;
};

// Knowledge Base API
export const getKnowledgeBase = async (category = null) => {
  const params = category ? { category } : {};
  const response = await axios.get(`${API_URL}/knowledge-base`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

// Inventory: Destructive clear-all
export const clearAllInventory = async () => {
  const response = await axios.delete(`${API_URL}/inventory/clear-all`, {
    headers: getAuthHeader(),
    params: { confirm: "DELETE-ALL-INVENTORY" },
  });
  return response.data;
};

// Inventory: Sub-Categories
export const getInventorySubcategories = async (categoryId = null) => {
  const params = categoryId ? { category_id: categoryId } : {};
  const r = await axios.get(`${API_URL}/inventory/subcategories`, { headers: getAuthHeader(), params });
  return r.data;
};
export const createInventorySubcategory = async (data) => (await axios.post(`${API_URL}/inventory/subcategories`, data, { headers: getAuthHeader() })).data;
export const updateInventorySubcategory = async (id, data) => (await axios.put(`${API_URL}/inventory/subcategories/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteInventorySubcategory = async (id) => (await axios.delete(`${API_URL}/inventory/subcategories/${id}`, { headers: getAuthHeader() })).data;

// Inventory: Item Groups
export const getInventoryItemGroups = async () => (await axios.get(`${API_URL}/inventory/item-groups`, { headers: getAuthHeader() })).data;
export const createInventoryItemGroup = async (data) => (await axios.post(`${API_URL}/inventory/item-groups`, data, { headers: getAuthHeader() })).data;
export const updateInventoryItemGroup = async (id, data) => (await axios.put(`${API_URL}/inventory/item-groups/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteInventoryItemGroup = async (id) => (await axios.delete(`${API_URL}/inventory/item-groups/${id}`, { headers: getAuthHeader() })).data;

// Inventory: Compatible Models
export const getInventoryCompatibleModels = async () => (await axios.get(`${API_URL}/inventory/compatible-models`, { headers: getAuthHeader() })).data;
export const createInventoryCompatibleModel = async (data) => (await axios.post(`${API_URL}/inventory/compatible-models`, data, { headers: getAuthHeader() })).data;
export const updateInventoryCompatibleModel = async (id, data) => (await axios.put(`${API_URL}/inventory/compatible-models/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteInventoryCompatibleModel = async (id) => (await axios.delete(`${API_URL}/inventory/compatible-models/${id}`, { headers: getAuthHeader() })).data;

// Inventory: Brands
export const getInventoryBrands = async () => (await axios.get(`${API_URL}/inventory/brands`, { headers: getAuthHeader() })).data;
export const createInventoryBrand = async (data) => (await axios.post(`${API_URL}/inventory/brands`, data, { headers: getAuthHeader() })).data;
export const updateInventoryBrand = async (id, data) => (await axios.put(`${API_URL}/inventory/brands/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteInventoryBrand = async (id) => (await axios.delete(`${API_URL}/inventory/brands/${id}`, { headers: getAuthHeader() })).data;

// Inventory: Variants (color/style values like Black, Blue, Red)
export const getInventoryVariants = async () => (await axios.get(`${API_URL}/inventory/variants`, { headers: getAuthHeader() })).data;
export const createInventoryVariant = async (data) => (await axios.post(`${API_URL}/inventory/variants`, data, { headers: getAuthHeader() })).data;
export const updateInventoryVariant = async (id, data) => (await axios.put(`${API_URL}/inventory/variants/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteInventoryVariant = async (id) => (await axios.delete(`${API_URL}/inventory/variants/${id}`, { headers: getAuthHeader() })).data;

// CMS: Hero auto-fit (smart-resize all carousel images to 2560×1080 with sign-bias crop)
export const autoFitHeroImages = async () => (await axios.post(`${API_URL}/cms/hero/auto-fit`, {}, { headers: getAuthHeader() })).data;

// CMS: One-click upload + AI car-removal + auto-fit (returns the new image's URL)
export const uploadCleanAndFitHero = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_URL}/cms/hero/upload-clean-fit`, fd, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// Business Plan + Org Chart draft autosave
export const getBusinessPlanDraft = async () => (await axios.get(`${API_URL}/dealer/business-plan-draft`, { headers: getAuthHeader() })).data;
export const saveBusinessPlanDraft = async (data) => (await axios.put(`${API_URL}/dealer/business-plan-draft`, { data }, { headers: getAuthHeader() })).data;

// Inventory: SKU/UPC generators
export const generateProductSku = async () => (await axios.get(`${API_URL}/inventory/products/generate-sku`, { headers: getAuthHeader() })).data;
export const generateProductUpc = async () => (await axios.get(`${API_URL}/inventory/products/generate-upc`, { headers: getAuthHeader() })).data;

// Inventory: Stock Viewer (one row per product, enriched)
export const getStockViewer = async () => (await axios.get(`${API_URL}/inventory/stock-viewer`, { headers: getAuthHeader() })).data;

// Marketplace: Cancel order with restock/remove decision
export const cancelMarketplaceOrder = async (orderId, restock, removalReason = "") => (await axios.post(`${API_URL}/inventory/marketplace/orders/${orderId}/cancel`, { restock, removal_reason: removalReason }, { headers: getAuthHeader() })).data;

// Inventory: Hard-delete endpoints (admin/Ray Kalasho only)
export const deleteReceiveHistory = async (id) => (await axios.delete(`${API_URL}/inventory/receive-history/${id}`, { headers: getAuthHeader() })).data;
export const deleteInventoryCorrection = async (id) => (await axios.delete(`${API_URL}/inventory/corrections/${id}`, { headers: getAuthHeader() })).data;
export const deleteMarketplaceOrder = async (id) => (await axios.delete(`${API_URL}/inventory/marketplace/orders/${id}`, { headers: getAuthHeader() })).data;

// Inventory removals report
export const getInventoryRemovals = async () => (await axios.get(`${API_URL}/inventory/removals`, { headers: getAuthHeader() })).data;

// Inventory audit log (hard-delete activity)
export const getInventoryAuditLog = async () => (await axios.get(`${API_URL}/inventory/audit-log`, { headers: getAuthHeader() })).data;

// Marketplace: notify-me when back in stock
export const requestStockNotification = async (productId) => (await axios.post(`${API_URL}/inventory/marketplace/notify-me`, { product_id: productId }, { headers: getAuthHeader() })).data;
export const cancelStockNotification = async (productId) => (await axios.delete(`${API_URL}/inventory/marketplace/notify-me/${productId}`, { headers: getAuthHeader() })).data;


// File Upload API
export const uploadFile = async (file, folder = "uploads") => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/upload?folder=${folder}`, formData, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "multipart/form-data"
    }
  });
  return response.data;
};

// Blog API
export const getBlogCategories = async () => {
  const response = await axios.get(`${API_URL}/blog/categories`);
  return response.data;
};

export const createBlogCategory = async (categoryData) => {
  const response = await axios.post(`${API_URL}/blog/categories`, categoryData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getBlogPosts = async (status = null, category = null, limit = 2000) => {
  const params = { limit };
  if (status) params.status = status;
  if (category) params.category = category;
  const response = await axios.get(`${API_URL}/blog/posts`, { params });
  return response.data;
};

export const getBlogPost = async (slug) => {
  const response = await axios.get(`${API_URL}/blog/posts/${slug}`);
  return response.data;
};

export const createBlogPost = async (postData) => {
  const response = await axios.post(`${API_URL}/blog/posts`, postData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateBlogPost = async (postId, postData) => {
  const response = await axios.put(`${API_URL}/blog/posts/${postId}`, postData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteBlogPost = async (postId) => {
  const response = await axios.delete(`${API_URL}/blog/posts/${postId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const bulkDeleteBlogPosts = async (ids) => {
  const response = await axios.delete(`${API_URL}/blog/posts/bulk`, {
    headers: getAuthHeader(),
    data: { ids },
  });
  return response.data;
};

// Gallery API
export const getGalleryItems = async (source = null, activeOnly = true, limit = 50) => {
  const params = { active_only: activeOnly, limit };
  if (source) params.source = source;
  const response = await axios.get(`${API_URL}/gallery`, { params });
  return response.data;
};

export const createGalleryItem = async (itemData) => {
  const response = await axios.post(`${API_URL}/gallery`, itemData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getOembedData = async (url) => {
  const response = await axios.get(`${API_URL}/gallery/oembed`, { params: { url }, headers: getAuthHeader() });
  return response.data;
};

export const updateGalleryItem = async (itemId, itemData) => {
  const response = await axios.put(`${API_URL}/gallery/${itemId}`, itemData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteGalleryItem = async (itemId) => {
  const response = await axios.delete(`${API_URL}/gallery/${itemId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Social Media Config API
export const getSocialMediaConfigs = async () => {
  const response = await axios.get(`${API_URL}/social-media/config`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateSocialMediaConfig = async (platform, configData) => {
  const response = await axios.put(`${API_URL}/social-media/config/${platform}`, configData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const syncSocialMedia = async (platform) => {
  const response = await axios.post(`${API_URL}/social-media/sync/${platform}`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ============== INVENTORY API ==============

// Categories
export const getInventoryCategories = async () => {
  const response = await axios.get(`${API_URL}/inventory/categories`, { headers: getAuthHeader() });
  return response.data;
};
export const createInventoryCategory = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/categories`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateInventoryCategory = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/categories/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteInventoryCategory = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/categories/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Conditions
export const getInventoryConditions = async () => {
  const response = await axios.get(`${API_URL}/inventory/conditions`, { headers: getAuthHeader() });
  return response.data;
};
export const createInventoryCondition = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/conditions`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateInventoryCondition = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/conditions/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteInventoryCondition = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/conditions/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Carrier Locks
export const getCarrierLocks = async () => {
  const response = await axios.get(`${API_URL}/inventory/carrier-locks`, { headers: getAuthHeader() });
  return response.data;
};
export const createCarrierLock = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/carrier-locks`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateCarrierLock = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/carrier-locks/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteCarrierLock = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/carrier-locks/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Vendors
export const getInventoryVendors = async () => {
  const response = await axios.get(`${API_URL}/inventory/vendors`, { headers: getAuthHeader() });
  return response.data;
};
export const createInventoryVendor = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/vendors`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateInventoryVendor = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/vendors/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteInventoryVendor = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/vendors/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Products
export const getInventoryProducts = async (categoryId = null) => {
  const params = categoryId ? { category_id: categoryId } : {};
  const response = await axios.get(`${API_URL}/inventory/products`, { headers: getAuthHeader(), params });
  return response.data;
};
export const createInventoryProduct = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/products`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateInventoryProduct = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/products/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteInventoryProduct = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/products/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Purchase Orders
export const getPurchaseOrders = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/inventory/purchase-orders`, { headers: getAuthHeader(), params });
  return response.data;
};
export const createPurchaseOrder = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/purchase-orders`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updatePurchaseOrder = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/purchase-orders/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deletePurchaseOrder = async (id) => {
  const response = await axios.delete(`${API_URL}/inventory/purchase-orders/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Receive Orders
export const receiveOrder = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/receive-orders`, data, { headers: getAuthHeader() });
  return response.data;
};
export const getReceiveHistory = async () => {
  const response = await axios.get(`${API_URL}/inventory/receive-history`, { headers: getAuthHeader() });
  return response.data;
};

// Corrections
export const getInventoryCorrections = async () => {
  const response = await axios.get(`${API_URL}/inventory/corrections`, { headers: getAuthHeader() });
  return response.data;
};
export const createInventoryCorrection = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/corrections`, data, { headers: getAuthHeader() });
  return response.data;
};

// Stock
export const getInventoryStock = async () => {
  const response = await axios.get(`${API_URL}/inventory/stock`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerStock = async () => {
  const response = await axios.get(`${API_URL}/inventory/stock/dealer`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerProductDetail = async (productId) => {
  const response = await axios.get(`${API_URL}/inventory/products/${productId}/detail`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerRepairStatus = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-repair-status`, { headers: getAuthHeader() });
  return response.data;
};

// Serial Numbers
export const searchSerialNumbers = async (q) => {
  const response = await axios.get(`${API_URL}/inventory/serial-search`, { headers: getAuthHeader(), params: { q } });
  return response.data;
};
export const getProductSerials = async (productId, status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/inventory/serials/${productId}`, { headers: getAuthHeader(), params });
  return response.data;
};

// Marketplace Orders
export const createMarketplaceOrder = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/marketplace/order`, data, { headers: getAuthHeader() });
  return response.data;
};
export const getMyMarketplaceOrders = async () => {
  const response = await axios.get(`${API_URL}/inventory/marketplace/my-orders`, { headers: getAuthHeader() });
  return response.data;
};
export const getAllMarketplaceOrders = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API_URL}/inventory/marketplace/orders`, { headers: getAuthHeader(), params });
  return response.data;
};
export const updateMarketplaceOrder = async (id, data) => {
  const response = await axios.put(`${API_URL}/inventory/marketplace/orders/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const getMarketplaceOrderDetail = async (id) => {
  const response = await axios.get(`${API_URL}/inventory/marketplace/orders/${id}`, { headers: getAuthHeader() });
  return response.data;
};
export const adminCreateMarketplaceOrder = async (data) => {
  const response = await axios.post(`${API_URL}/inventory/marketplace/orders/admin-create`, data, { headers: getAuthHeader() });
  return response.data;
};
export const getAvailableSerialsForOrder = async (orderId, productId) => {
  const response = await axios.get(`${API_URL}/inventory/marketplace/orders/${orderId}/available-serials/${productId}`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealersForOrder = async () => {
  const response = await axios.get(`${API_URL}/inventory/marketplace/dealers-list`, { headers: getAuthHeader() });
  return response.data;
};
export const getMyReceivedSerials = async () => {
  const response = await axios.get(`${API_URL}/inventory/marketplace/my-serials`, { headers: getAuthHeader() });
  return response.data;
};
// Knowledge Base admin CRUD
export const createKBArticle = async (data) => {
  const response = await axios.post(`${API_URL}/knowledge-base`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateKBArticle = async (id, data) => {
  const response = await axios.put(`${API_URL}/knowledge-base/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteKBArticle = async (id) => {
  const response = await axios.delete(`${API_URL}/knowledge-base/${id}`, { headers: getAuthHeader() });
  return response.data;
};
// Training admin CRUD
export const createTrainingMaterial = async (data) => {
  const response = await axios.post(`${API_URL}/training`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateTrainingMaterial = async (id, data) => {
  const response = await axios.put(`${API_URL}/training/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteTrainingMaterial = async (id) => {
  const response = await axios.delete(`${API_URL}/training/${id}`, { headers: getAuthHeader() });
  return response.data;
};
// Dealer submit new location
export const dealerSubmitLocation = async (data) => {
  const response = await axios.post(`${API_URL}/dealer/submit-location`, data, { headers: getAuthHeader() });
  return response.data;
};
// Dealer my locations
export const getDealerSubmittedLocations = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-submitted-locations`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerAssignedLocations = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-assigned-locations`, { headers: getAuthHeader() });
  return response.data;
};
// Dealer self-service employee management
export const getMyEmployees = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-employees`, { headers: getAuthHeader() });
  return response.data;
};
export const createMyEmployee = async (data) => {
  const response = await axios.post(`${API_URL}/dealer/my-employees`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateMyEmployee = async (empId, data) => {
  const response = await axios.put(`${API_URL}/dealer/my-employees/${empId}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteMyEmployee = async (empId) => {
  const response = await axios.delete(`${API_URL}/dealer/my-employees/${empId}`, { headers: getAuthHeader() });
  return response.data;
};
// Dealer notifications
export const getDealerNotifications = async () => {
  const response = await axios.get(`${API_URL}/dealer/notifications`, { headers: getAuthHeader() });
  return response.data;
};
export const getUnreadNotificationCount = async () => {
  const response = await axios.get(`${API_URL}/dealer/notifications/unread-count`, { headers: getAuthHeader() });
  return response.data;
};
export const markNotificationRead = async (notifId) => {
  const response = await axios.put(`${API_URL}/dealer/notifications/${notifId}/read`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const markAllNotificationsRead = async () => {
  const response = await axios.put(`${API_URL}/dealer/notifications/read-all`, {}, { headers: getAuthHeader() });
  return response.data;
};
// Training completions
export const completeTraining = async (materialId) => {
  const response = await axios.post(`${API_URL}/dealer/training/${materialId}/complete`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const getTrainingCompletions = async () => {
  const response = await axios.get(`${API_URL}/dealer/training/completions`, { headers: getAuthHeader() });
  return response.data;
};
export const recordVideoComplete = async (materialId) => {
  const response = await axios.post(`${API_URL}/dealer/training/${materialId}/video-complete`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const submitTrainingQuiz = async (materialId, answers) => {
  const response = await axios.post(`${API_URL}/dealer/training/${materialId}/quiz-submit`, { answers }, { headers: getAuthHeader() });
  return response.data;
};
export const getTrainingProgress = async (materialId) => {
  const response = await axios.get(`${API_URL}/dealer/training/${materialId}/progress`, { headers: getAuthHeader() });
  return response.data;
};
// KB categories CRUD
export const getKBCategories = async () => {
  const response = await axios.get(`${API_URL}/knowledge-base/categories`);
  return response.data;
};
export const createKBCategory = async (data) => {
  const response = await axios.post(`${API_URL}/knowledge-base/categories`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateKBCategory = async (id, data) => {
  const response = await axios.put(`${API_URL}/knowledge-base/categories/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteKBCategory = async (id) => {
  const response = await axios.delete(`${API_URL}/knowledge-base/categories/${id}`, { headers: getAuthHeader() });
  return response.data;
};
// Training categories CRUD
export const getTrainingCategories = async () => {
  const response = await axios.get(`${API_URL}/training/categories`);
  return response.data;
};
export const createTrainingCategory = async (data) => {
  const response = await axios.post(`${API_URL}/training/categories`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateTrainingCategory = async (id, data) => {
  const response = await axios.put(`${API_URL}/training/categories/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteTrainingCategory = async (id) => {
  const response = await axios.delete(`${API_URL}/training/categories/${id}`, { headers: getAuthHeader() });
  return response.data;
};
// Blog categories edit/delete
export const updateBlogCategory = async (id, data) => {
  const response = await axios.put(`${API_URL}/blog/categories/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteBlogCategory = async (id) => {
  const response = await axios.delete(`${API_URL}/blog/categories/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// ============== APPLICATION CREDENTIALS API ==============
export const getApplicationCredentials = async (appId) => {
  const response = await axios.get(`${API_URL}/dealer-applications/${appId}/credentials`, { headers: getAuthHeader() });
  return response.data;
};
export const setApplicationCredentials = async (appId, data) => {
  const response = await axios.post(`${API_URL}/dealer-applications/${appId}/credentials`, data, { headers: getAuthHeader() });
  return response.data;
};

// ============== DEALER EMPLOYEES API ==============
export const adminCreateDealer = async (data) => {
  const response = await axios.post(`${API_URL}/dealers/admin-create`, data, { headers: getAuthHeader() });
  return response.data;
};
export const bulkUploadDealers = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/dealers/bulk-upload`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};
export const getDealerEmployees = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/employees`, { headers: getAuthHeader() });
  return response.data;
};

export const getDealerOwnerAccount = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/owner-account`, { headers: getAuthHeader() });
  return response.data;
};

export const updateDealerOwnerAccount = async (dealerId, data) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/owner-account`, data, { headers: getAuthHeader() });
  return response.data;
};

// Admin: Dealer Document Review & Management
export const adminGetRequiredDocuments = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/required-documents`, { headers: getAuthHeader() });
  return response.data;
};
export const adminGetPreApprovedDocuments = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/pre-approved-documents`, { headers: getAuthHeader() });
  return response.data;
};
export const adminGetPreApprovedData = async (dealerId) => {
  const response = await axios.get(`${API_URL}/dealers/${dealerId}/pre-approved-data`, { headers: getAuthHeader() });
  return response.data;
};
export const adminUpdatePreApprovedData = async (dealerId, body) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/pre-approved-data`, body, { headers: getAuthHeader() });
  return response.data;
};
export const adminDeletePreApprovedDocument = async (dealerId, docId) => {
  const response = await axios.delete(`${API_URL}/dealers/${dealerId}/pre-approved-documents/${docId}`, { headers: getAuthHeader() });
  return response.data;
};
export const reviewDealerDocument = async (dealerId, docId, data) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/documents/${docId}/review`, data, { headers: getAuthHeader() });
  return response.data;
};
export const reviewPreApprovedDocument = async (dealerId, docId, data) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/pre-approved-documents/${docId}/review`, data, { headers: getAuthHeader() });
  return response.data;
};
export const createDealerEmployee = async (dealerId, data) => {
  const response = await axios.post(`${API_URL}/dealers/${dealerId}/employees`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateDealerEmployee = async (dealerId, empId, data) => {
  const response = await axios.put(`${API_URL}/dealers/${dealerId}/employees/${empId}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteDealerEmployee = async (dealerId, empId) => {
  const response = await axios.delete(`${API_URL}/dealers/${dealerId}/employees/${empId}`, { headers: getAuthHeader() });
  return response.data;
};

// ============== LOCATIONS BULK UPLOAD API ==============
export const bulkUploadLocations = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/locations/bulk-upload`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

// ============== APPLICANT DOCUMENTS API ==============
export const getApplicantDocuments = async () => {
  const response = await axios.get(`${API_URL}/applicant/my-documents`, { headers: getAuthHeader() });
  return response.data;
};
export const uploadApplicantDocument = async (file, docType = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${API_URL}/applicant/my-documents?doc_type=${docType}`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};


// ========== DEALER VISITS ==========
export const getDealerVisits = async (params = {}) => {
  const response = await axios.get(`${API_URL}/dealer-visits`, { headers: getAuthHeader(), params });
  return response.data;
};
export const getDealerVisitStats = async () => {
  const response = await axios.get(`${API_URL}/dealer-visits/stats`, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerVisit = async (id) => {
  const response = await axios.get(`${API_URL}/dealer-visits/${id}`, { headers: getAuthHeader() });
  return response.data;
};
export const createDealerVisit = async (data) => {
  const response = await axios.post(`${API_URL}/dealer-visits`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateDealerVisit = async (id, data) => {
  const response = await axios.put(`${API_URL}/dealer-visits/${id}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteDealerVisit = async (id) => {
  const response = await axios.delete(`${API_URL}/dealer-visits/${id}`, { headers: getAuthHeader() });
  return response.data;
};
export const visitCheckIn = async (id) => {
  const response = await axios.post(`${API_URL}/dealer-visits/${id}/check-in`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const visitCheckOut = async (id, data) => {
  const response = await axios.post(`${API_URL}/dealer-visits/${id}/check-out`, data, { headers: getAuthHeader() });
  return response.data;
};
export const addVisitPhoto = async (id, data) => {
  const response = await axios.post(`${API_URL}/dealer-visits/${id}/photos`, data, { headers: getAuthHeader() });
  return response.data;
};
export const getMyDealerVisits = async () => {
  const response = await axios.get(`${API_URL}/dealer/my-visits`, { headers: getAuthHeader() });
  return response.data;
};


// ========== REPSLY ==========
export const getRepslySettings = async () => {
  const response = await axios.get(`${API_URL}/repsly/settings`, { headers: getAuthHeader() });
  return response.data;
};
export const updateRepslySettings = async (data) => {
  const response = await axios.put(`${API_URL}/repsly/settings`, data, { headers: getAuthHeader() });
  return response.data;
};
export const triggerRepslySync = async () => {
  const response = await axios.post(`${API_URL}/repsly/sync`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const testRepslyConnection = async (data) => {
  const response = await axios.post(`${API_URL}/repsly/test-connection`, data, { headers: getAuthHeader() });
  return response.data;
};

// FAQ API
export const getFaqCategories = async (activeOnly = false) => {
  const response = await axios.get(`${API_URL}/faq/categories`, { params: { active_only: activeOnly } });
  return response.data;
};
export const createFaqCategory = async (data) => {
  const response = await axios.post(`${API_URL}/faq/categories`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateFaqCategory = async (catId, data) => {
  const response = await axios.put(`${API_URL}/faq/categories/${catId}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteFaqCategory = async (catId) => {
  const response = await axios.delete(`${API_URL}/faq/categories/${catId}`, { headers: getAuthHeader() });
  return response.data;
};
export const getFaqItems = async (categoryId = null, activeOnly = false) => {
  const params = { active_only: activeOnly };
  if (categoryId) params.category_id = categoryId;
  const response = await axios.get(`${API_URL}/faq/items`, { params });
  return response.data;
};
export const createFaqItem = async (data) => {
  const response = await axios.post(`${API_URL}/faq/items`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateFaqItem = async (itemId, data) => {
  const response = await axios.put(`${API_URL}/faq/items/${itemId}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteFaqItem = async (itemId) => {
  const response = await axios.delete(`${API_URL}/faq/items/${itemId}`, { headers: getAuthHeader() });
  return response.data;
};


// Career / Job Position API
export const getCareerPositions = async (activeOnly = false, locationId = null, positionType = null) => {
  const params = {};
  if (activeOnly) params.active_only = true;
  if (locationId) params.location_id = locationId;
  if (positionType) params.position_type = positionType;
  const response = await axios.get(`${API_URL}/careers/positions`, { params });
  return response.data;
};
export const getCareerPosition = async (positionId) => {
  const response = await axios.get(`${API_URL}/careers/positions/${positionId}`);
  return response.data;
};
export const createCareerPosition = async (data) => {
  const response = await axios.post(`${API_URL}/careers/positions`, data, { headers: getAuthHeader() });
  return response.data;
};
export const updateCareerPosition = async (positionId, data) => {
  const response = await axios.put(`${API_URL}/careers/positions/${positionId}`, data, { headers: getAuthHeader() });
  return response.data;
};
export const deleteCareerPosition = async (positionId) => {
  const response = await axios.delete(`${API_URL}/careers/positions/${positionId}`, { headers: getAuthHeader() });
  return response.data;
};
export const bulkGeneratePositions = async () => {
  const response = await axios.post(`${API_URL}/careers/positions/bulk-generate`, {}, { headers: getAuthHeader() });
  return response.data;
};
export const deleteAllCareerPositions = async () => {
  const response = await axios.delete(`${API_URL}/careers/positions/all`, { headers: getAuthHeader() });
  return response.data;
};
export const bulkUpdatePositionsByType = async (data) => {
  const response = await axios.put(`${API_URL}/careers/positions/bulk-update-type`, data, { headers: getAuthHeader() });
  return response.data;
};


export const submitCareerApplication = async (formData) => {
  const response = await axios.post(`${API_URL}/careers/apply`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    params: {
      position_id: formData.get("position_id"),
      first_name: formData.get("first_name"),
      last_name: formData.get("last_name"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      cover_letter: formData.get("cover_letter") || "",
      street: formData.get("street") || "",
      city: formData.get("city") || "",
      state: formData.get("state") || "",
      zip_code: formData.get("zip_code") || "",
    },
  });
  return response.data;
};
export const getCareerApplications = async (locationId = null, status = null, positionType = null) => {
  const params = {};
  if (locationId) params.location_id = locationId;
  if (status) params.status = status;
  if (positionType) params.position_type = positionType;
  const response = await axios.get(`${API_URL}/careers/applications`, { headers: getAuthHeader(), params });
  return response.data;
};
export const updateCareerApplicationStatus = async (appId, status) => {
  const response = await axios.put(`${API_URL}/careers/applications/${appId}/status`, { status }, { headers: getAuthHeader() });
  return response.data;
};
export const getDealerCareerApplications = async () => {
  const response = await axios.get(`${API_URL}/dealer/career-applications`, { headers: getAuthHeader() });
  return response.data;
};
export const updateDealerCareerApplicationInfo = async (appId, data) => {
  const response = await axios.put(`${API_URL}/dealer/career-applications/${appId}/info`, data, { headers: getAuthHeader() });
  return response.data;
};


export const deleteCareerApplication = async (appId) => {
  const response = await axios.delete(`${API_URL}/careers/applications/${appId}`, { headers: getAuthHeader() });
  return response.data;
};
export const updateCareerApplicationNotes = async (appId, notes) => {
  const response = await axios.put(`${API_URL}/careers/applications/${appId}/notes`, { notes }, { headers: getAuthHeader() });
  return response.data;
};

export const updateCareerApplicationInfo = async (appId, data) => {
  const response = await axios.put(`${API_URL}/careers/applications/${appId}/info`, data, { headers: getAuthHeader() });
  return response.data;
};


// Email Templates API
export const getEmailTemplates = async () => {
  const response = await axios.get(`${API_URL}/email-templates`, { headers: getAuthHeader() });
  return response.data;
};

export const updateEmailTemplate = async (slug, data) => {
  const response = await axios.put(`${API_URL}/email-templates/${slug}`, data, { headers: getAuthHeader() });
  return response.data;
};

export const testEmailTemplate = async (slug) => {
  const response = await axios.post(`${API_URL}/email-templates/${slug}/test`, {}, { headers: getAuthHeader() });
  return response.data;
};


// Reports API
export const listReports = async () => {
  const response = await axios.get(`${API_URL}/reports/list`, { headers: getAuthHeader() });
  return response.data;
};

export const getAuditLog = async (params = {}) => {
  const response = await axios.get(`${API_URL}/reports/audit-log`, {
    headers: getAuthHeader(),
    params,
  });
  return response.data;
};

export const getAuditLogFilters = async () => {
  const response = await axios.get(`${API_URL}/reports/audit-log/filters`, {
    headers: getAuthHeader(),
  });
  return response.data;
};


// Analytics API
export const getAnalyticsSummary = async () => {
  const response = await axios.get(`${API_URL}/analytics/summary`, { headers: getAuthHeader() });
  return response.data;
};

export const getDealerSignups = async (days = 90, bucket = "day") => {
  const response = await axios.get(`${API_URL}/analytics/dealer-signups`, {
    headers: getAuthHeader(),
    params: { days, bucket },
  });
  return response.data;
};

export const getTopPages = async (days = 30, limit = 20) => {
  const response = await axios.get(`${API_URL}/analytics/top-pages`, {
    headers: getAuthHeader(),
    params: { days, limit },
  });
  return response.data;
};

export const getApplicationFunnel = async () => {
  const response = await axios.get(`${API_URL}/analytics/application-funnel`, {
    headers: getAuthHeader(),
  });
  return response.data;
};

export const getEmailStats = async (days = 30) => {
  const response = await axios.get(`${API_URL}/analytics/email-stats`, {
    headers: getAuthHeader(),
    params: { days },
  });
  return response.data;
};

export const recordPageview = async (path) => {
  try {
    await axios.post(`${API_URL}/analytics/pageview`, {
      path,
      referrer: document.referrer || "",
      utm_source: new URLSearchParams(window.location.search).get("utm_source") || "",
    });
  } catch (e) {
    // Silent — tracking failures should never break the page
  }
};

export const getMostViewedPages = async (days = 30, limit = 15) => {
  const response = await axios.get(`${API_URL}/analytics/most-viewed-pages`, {
    headers: getAuthHeader(),
    params: { days, limit },
  });
  return response.data;
};

export const getStatePageRankings = async (days = 30) => {
  const response = await axios.get(`${API_URL}/analytics/state-page-rankings`, {
    headers: getAuthHeader(),
    params: { days },
  });
  return response.data;
};
export const getDealerCoOwners = async (dealerId) => (await axios.get(`${API_URL}/dealers/${dealerId}/co-owners`, { headers: getAuthHeader() })).data;
export const createDealerCoOwner = async (dealerId, data) => (await axios.post(`${API_URL}/dealers/${dealerId}/co-owners`, data, { headers: getAuthHeader() })).data;
export const updateDealerCoOwner = async (dealerId, uid, data) => (await axios.put(`${API_URL}/dealers/${dealerId}/co-owners/${uid}`, data, { headers: getAuthHeader() })).data;
export const deleteDealerCoOwner = async (dealerId, uid) => (await axios.delete(`${API_URL}/dealers/${dealerId}/co-owners/${uid}`, { headers: getAuthHeader() })).data;
export const getAdminRoles = async () => (await axios.get(`${API_URL}/admin/roles`, { headers: getAuthHeader() })).data;
export const createAdminRole = async (data) => (await axios.post(`${API_URL}/admin/roles`, data, { headers: getAuthHeader() })).data;
export const updateAdminRole = async (id, data) => (await axios.put(`${API_URL}/admin/roles/${id}`, data, { headers: getAuthHeader() })).data;
export const deleteAdminRole = async (id) => (await axios.delete(`${API_URL}/admin/roles/${id}`, { headers: getAuthHeader() })).data;
export const getPermissionAreas = async () => (await axios.get(`${API_URL}/admin/permission-areas`, { headers: getAuthHeader() })).data;
